import React, {useEffect, useMemo, useState} from 'react';
import {Button, Checkbox, Dimmer, Grid, Input, Loader, Modal, Table} from 'semantic-ui-react';
import {apiPost} from "../../../services/apiClient";
import {toast} from "react-toastify";
import {CircleMarker, Map, TileLayer, Tooltip} from 'react-leaflet';

import {getMapApiEntries} from "./functions/pvzAddress";

const pvzToEndpoint = (isReception) => isReception
    ? 'parcels/cdek/pickuppoints/map/from'
    : 'parcels/cdek/pickuppoints/map/to';

const normalizePvzAddressDisplay = (addressFull) => {
    const source = String(addressFull || '').trim();
    if (!source) return '';

    const normalizeToken = (v) => String(v || '')
        .toLowerCase()
        .replace(/^город\s+/i, '')
        .replace(/^г\.\s*/i, '')
        .replace(/^г\s+/i, '')
        .trim();

    const parts = source
        .split(',')
        .map(x => x.trim())
        .filter(Boolean);

    const deduped = [];
    for (let i = 0; i < parts.length; i++) {
        const prev = deduped.length ? deduped[deduped.length - 1] : null;
        if (prev && normalizeToken(prev) === normalizeToken(parts[i])) {
            continue;
        }
        deduped.push(parts[i]);
    }

    return deduped.join(', ');
};

const PvzSelectMapModal = ({open, onClose, postalCode, city, centerLat, centerLon, isReception, onSelect, weight = null}) => {
    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState([]);
    const [mapBounds, setMapBounds] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    /** Фильтр только для таблицы слева; на карте всегда все точки НП, без ограничения видимой областью. */
    const [showOnlyInViewport, setShowOnlyInViewport] = useState(false);

    const endpointsPostalCode = postalCode ? String(postalCode).trim() : '';
    const normalizedCity = city ? String(city).trim() : '';
    const normalizedCenterLat = centerLat === null || centerLat === undefined || centerLat === '' ? null : Number(centerLat);
    const normalizedCenterLon = centerLon === null || centerLon === undefined || centerLon === '' ? null : Number(centerLon);

    const hasCenter = Number.isFinite(normalizedCenterLat) && Number.isFinite(normalizedCenterLon);
    const mapCenter = useMemo(() => {
        if (hasCenter) {
            return [normalizedCenterLat, normalizedCenterLon];
        }
        const p = points.find(x => x.latitude != null && x.longitude != null);
        if (!p) return [0, 0];
        return [Number(p.latitude), Number(p.longitude)];
    }, [points, hasCenter, normalizedCenterLat, normalizedCenterLon]);

    useEffect(() => {
        if (!open) return;

        if (!endpointsPostalCode && !normalizedCity) {
            setPoints([]);
            return;
        }

        const requestWithCenter = {
            postalCode: endpointsPostalCode,
            city: normalizedCity,
            centerLat: hasCenter ? normalizedCenterLat : null,
            centerLon: hasCenter ? normalizedCenterLon : null,
            fullList: true,
            weight: weight == null || weight === '' || Number(weight) <= 0 || isNaN(Number(weight)) ? null : Number(weight)
        };

        setLoading(true);
        apiPost(pvzToEndpoint(isReception), requestWithCenter)
            .then(data => {
                const entries = getMapApiEntries(data);
                if (data && (data.warningMessage || data.WarningMessage)) {
                    toast.warning(data.warningMessage || data.WarningMessage);
                }
                if (entries.length > 0 || !hasCenter) {
                    setPoints(entries);
                    setLoading(false);
                    return;
                }

                apiPost(pvzToEndpoint(isReception), {
                    postalCode: endpointsPostalCode,
                    city: normalizedCity,
                    fullList: true,
                    weight: requestWithCenter.weight
                })
                    .then(fallbackData => {
                        if (fallbackData && (fallbackData.warningMessage || fallbackData.WarningMessage)) {
                            toast.warning(fallbackData.warningMessage || fallbackData.WarningMessage);
                        }
                        setPoints(getMapApiEntries(fallbackData));
                        setLoading(false);
                    })
                    .catch(err => {
                        setLoading(false);
                        toast.error('Не удалось загрузить точки ПВЗ');
                        console.log(err);
                    });
            })
            .catch(err => {
                setLoading(false);
                toast.error('Не удалось загрузить точки ПВЗ');
                console.log(err);
            });
    }, [open, endpointsPostalCode, normalizedCity, isReception, hasCenter, normalizedCenterLat, normalizedCenterLon, weight]);

    const mapHeight = '74vh';
    const mapStyle = {width: '100%', height: mapHeight};
    const mapZoom = hasCenter ? 16 : 12;
    const pointsInViewport = useMemo(() => {
        if (!mapBounds || !showOnlyInViewport) {
            return points;
        }

        const southWest = mapBounds[0] || [];
        const northEast = mapBounds[1] || [];
        const minLat = Number(southWest[0]);
        const minLon = Number(southWest[1]);
        const maxLat = Number(northEast[0]);
        const maxLon = Number(northEast[1]);

        if (!Number.isFinite(minLat) || !Number.isFinite(minLon) || !Number.isFinite(maxLat) || !Number.isFinite(maxLon)) {
            return points;
        }

        return points.filter(p => {
            const lat = Number(p.latitude);
            const lon = Number(p.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                return false;
            }

            return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
        });
    }, [points, mapBounds, showOnlyInViewport]);

    const pointsBySearch = useMemo(() => {
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return pointsInViewport;

        return pointsInViewport.filter(p => {
            const code = String((p.code || p.Code) || '').toLowerCase();
            const address = String((p.addressFull || p.AddressFull) || '').toLowerCase();
            return code.includes(q) || address.includes(q);
        });
    }, [pointsInViewport, searchQuery]);

    return (
        <Modal
            className='pvz-map-modal'
            dimmer={{ className: 'pvz-map-modal-dimmer' }}
            open={open}
            closeIcon
            onClose={onClose}
        >
            <Modal.Header>
                Выбор ПВЗ на карте
            </Modal.Header>
            <Modal.Content className='modal__content'>
                <Dimmer active={loading} inverted>
                    <Loader size='large'/>
                </Dimmer>

                <Grid columns={2} divided style={{height: mapHeight, width: '100%', margin: 0}}>
                    <Grid.Column width={6}>
                        <div style={{height: mapHeight, overflowY: 'auto', paddingRight: '10px'}}>
                            <div style={{marginBottom: '8px'}}>
                                <Input
                                    fluid
                                    icon='search'
                                    iconPosition='left'
                                    placeholder='Поиск по адресу или коду ПВЗ...'
                                    value={searchQuery}
                                    onChange={(e, data) => setSearchQuery((data && data.value) || '')}
                                />
                            </div>
                            <div style={{marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Checkbox
                                    label='Только в области карты (список)'
                                    checked={showOnlyInViewport}
                                    onChange={(e, data) => setShowOnlyInViewport(!!data.checked)}
                                />
                                <span style={{fontSize: '12px', color: '#6b7280'}}>
                                    {pointsBySearch.length} из {points.length}
                                </span>
                            </div>
                            <Table celled selectable compact>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Код</Table.HeaderCell>
                                        <Table.HeaderCell>Адрес</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {pointsBySearch.map(p => (
                                        <Table.Row key={p.code} onClick={() => onSelect && onSelect(p, { entries: points })}>
                                            <Table.Cell>{p.code}</Table.Cell>
                                            <Table.Cell>{normalizePvzAddressDisplay(p.addressFull || p.AddressFull)}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                    {!pointsBySearch.length && (
                                        <Table.Row>
                                            <Table.Cell colSpan={2}>
                                                Нет подходящих ПВЗ. Вернитесь на предыдущий экран для изменения населённого пункта.
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    </Grid.Column>
                    <Grid.Column width={10}>
                        <div style={{height: mapHeight}}>
                            <Map
                                style={mapStyle}
                                center={mapCenter}
                                zoom={mapZoom}
                                onmoveend={(e) => {
                                    const target = e && e.target ? e.target : null;
                                    const bounds = target && target.getBounds ? target.getBounds() : null;
                                    if (!bounds || !bounds.getSouthWest || !bounds.getNorthEast) {
                                        setMapBounds(null);
                                        return;
                                    }

                                    const southWest = bounds.getSouthWest();
                                    const northEast = bounds.getNorthEast();
                                    setMapBounds([
                                        [southWest.lat, southWest.lng],
                                        [northEast.lat, northEast.lng]
                                    ]);
                                }}
                                onzoomend={(e) => {
                                    const target = e && e.target ? e.target : null;
                                    const bounds = target && target.getBounds ? target.getBounds() : null;
                                    if (!bounds || !bounds.getSouthWest || !bounds.getNorthEast) {
                                        setMapBounds(null);
                                        return;
                                    }

                                    const southWest = bounds.getSouthWest();
                                    const northEast = bounds.getNorthEast();
                                    setMapBounds([
                                        [southWest.lat, southWest.lng],
                                        [northEast.lat, northEast.lng]
                                    ]);
                                }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {pointsBySearch.map(p => {
                                    if (p.latitude == null || p.longitude == null) return null;
                                    const code = ((p.code || p.Code) || '').toUpperCase();
                                    const address = normalizePvzAddressDisplay((p.addressFull || p.AddressFull) || '-') || '-';
                                    const workTime = (p.workTime || p.WorkTime) || '';
                                    const markerKey = `${code || 'pvz'}_${p.latitude}_${p.longitude}`;
                                    return (
                                        <CircleMarker
                                            key={markerKey}
                                            center={[Number(p.latitude), Number(p.longitude)]}
                                            radius={18}
                                            color={'#2563eb'}
                                            fillColor={'#3b82f6'}
                                            fillOpacity={0.7}
                                            onClick={() => onSelect && onSelect(p, { entries: points })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Tooltip direction="top" offset={[0, -18]} opacity={1}>
                                                <div
                                                    className='pvz-map-tooltip-inner'
                                                    style={{
                                                        fontSize: '13px',
                                                        lineHeight: 1.45,
                                                        minWidth: '280px',
                                                        maxWidth: 'min(480px, 85vw)',
                                                        padding: '12px 14px',
                                                        textAlign: 'left',
                                                        wordBreak: 'break-word',
                                                        whiteSpace: 'normal',
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <div style={{fontWeight: 700, marginBottom: '6px'}}>{code || 'ПВЗ'}</div>
                                                    <div style={{marginBottom: workTime ? '8px' : 0}}>{address}</div>
                                                    {!!workTime && <div style={{color: '#374151'}}>Часы: {workTime}</div>}
                                                </div>
                                            </Tooltip>
                                        </CircleMarker>
                                    );
                                })}
                            </Map>
                        </div>
                    </Grid.Column>
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>
                    Закрыть
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default PvzSelectMapModal;




// WEBPACK FOOTER //
// ./App/pages/parcels/form_v3/pvzSelectMapModal.jsx